import { Component } from '@theme/component';

/**
 * `<echt-delivery-countdown>` — "Order within 2 hrs 30 mins" beside the
 * delivery date. Rendered empty, so with this file blocked the row reads as the
 * date alone. `data-cutoff` is the next cutoff in epoch seconds, from the same
 * snippet that rendered the date, so the two cannot disagree; `data-label`
 * keeps the copy in Liquid. See docs/sections/product.md.
 *
 * @extends {Component}
 */
class EchtDeliveryCountdown extends Component {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;

  /** Cutoff in epoch ms, NaN when unreadable. */
  #target = NaN;

  connectedCallback() {
    super.connectedCallback();
    this.#target = Number(this.dataset.cutoff) * 1000;
    this.#tick();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#timer);
  }

  #tick() {
    const left = this.#target - Date.now();
    const day = 86400000;

    if (!Number.isFinite(left) || left <= 0) {
      this.#clear();
      return;
    }

    // Further out than a day — a weekend, mostly. "Order within 52 hrs" is
    // useless: say nothing until it is worth saying.
    if (left > day) {
      this.#clear();
      this.#timer = setTimeout(() => this.#tick(), left - day);
      return;
    }

    const total = Math.ceil(left / 60000);
    const hours = Math.floor(total / 60);
    const mins = total % 60;
    const parts = [];
    if (hours > 0) parts.push(`${hours} ${hours === 1 ? 'hr' : 'hrs'}`);
    if (mins > 0 || hours === 0) parts.push(`${mins} ${mins === 1 ? 'min' : 'mins'}`);
    this.textContent = `${this.dataset.label || 'Order within'} ${parts.join(' ')}`;

    this.hidden = false;
    this.#timer = setTimeout(() => this.#tick(), left % 60000 || 60000);
  }

  #clear() {
    this.textContent = '';
    if (this.hasAttribute('data-hidden-after')) this.hidden = true;
  }
}

if (!customElements.get('echt-delivery-countdown')) {
  customElements.define('echt-delivery-countdown', EchtDeliveryCountdown);
}
